import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api, setToken, setUnauthorizedHandler } from '../services/api';
import {
  beginTrustBridgeResume,
  connectWallet,
  disconnectWallet,
  hasInjectedProvider,
  waitForInAppDappWallet,
} from '../services/wallet';

const WalletContext = createContext(null);
export const UPLINE_STORAGE_KEY = 'dynafi_upline';

export function isWalletAddress(value) {
  return /^0x[a-fA-F0-9]{40}$/.test(String(value || '').trim());
}

export function readStoredUpline() {
  try {
    const v = localStorage.getItem(UPLINE_STORAGE_KEY);
    return isWalletAddress(v) ? v.toLowerCase() : '';
  } catch {
    return '';
  }
}

export function writeStoredUpline(wallet) {
  const next = isWalletAddress(wallet) ? String(wallet).trim().toLowerCase() : '';
  try {
    if (next) localStorage.setItem(UPLINE_STORAGE_KEY, next);
    else localStorage.removeItem(UPLINE_STORAGE_KEY);
  } catch {
    // ignore
  }
  return next;
}

export function WalletProvider({ children }) {
  const [address, setAddress] = useState('');
  const [user, setUser] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectOpening, setConnectOpening] = useState(false);
  const [connectReason, setConnectReason] = useState('');
  /** 'picker' | 'register' | 'pay' — modal only opens when needed */
  const [connectMode, setConnectMode] = useState('picker');
  const [pendingWalletId, setPendingWalletId] = useState('injected');
  const [error, setError] = useState('');
  const [referralUpline, setReferralUplineState] = useState(readStoredUpline);
  const inAppBusyRef = useRef(false);

  const setReferralUpline = useCallback((wallet) => {
    const next = writeStoredUpline(wallet);
    setReferralUplineState(next);
    return next;
  }, []);

  const refreshConfig = useCallback(async () => {
    const data = await api.getConfig();
    setConfig(data.config);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.me();
      setUser(data.user);
      return data.user;
    } catch {
      setUser(null);
      setToken('');
      return null;
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setAddress('');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await refreshConfig();
        // Session is in-memory only — never restore from storage after refresh
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshConfig]);

  const closeConnect = useCallback(() => {
    setConnectOpen(false);
    setConnectReason('');
    setConnectMode('picker');
  }, []);

  /** Step 1: connect wallet + try login. If unknown → needs_register. */
  const connectAndAuth = useCallback(
    async (walletId = 'injected') => {
      setError('');
      const { address: wallet } = await connectWallet(walletId, { forcePermission: true });
      setAddress(wallet);
      try {
        const login = await api.walletLogin(wallet);
        setToken(login.token);
        setUser(login.user);
        closeConnect();
        return { status: 'logged_in', user: login.user };
      } catch (e) {
        if (e.code === 'NOT_REGISTERED' || e.status === 404) {
          return { status: 'needs_register', wallet };
        }
        throw e;
      }
    },
    [closeConnect]
  );

  useEffect(() => {
    const task = beginTrustBridgeResume(() => connectAndAuth('trust'));
    if (!task) return undefined;
    let active = true;
    task
      .then((result) => {
        if (!active || !result) return;
        if (result.status === 'needs_register') {
          setPendingWalletId('trust');
          setConnectMode('register');
          setConnectOpen(true);
        }
      })
      .catch((e) => {
        if (!active) return;
        setError(e.message || 'Connection failed');
        setConnectMode('picker');
        setConnectOpen(true);
      });
    return () => {
      active = false;
    };
  }, [connectAndAuth]);

  /**
   * Open connect flow. Inside Trust / other dApp browsers: auto-connect
   * without showing the wallet picker modal. Modal only opens for
   * registration or if auto-connect fails.
   */
  const openConnect = useCallback(
    async (reason = '') => {
      setConnectReason(reason || '');
      setError('');

      if (inAppBusyRef.current) return;
      inAppBusyRef.current = true;
      setConnectOpening(true);
      try {
        const inAppId = await waitForInAppDappWallet(2500);
        if (inAppId) {
          setPendingWalletId(inAppId);
          try {
            const result = await connectAndAuth(inAppId);
            if (result.status === 'needs_register') {
              setConnectMode('register');
              setConnectOpen(true);
            }
            // logged_in → modal never opened
            return;
          } catch (e) {
            setError(e.message || 'Connection failed');
            // Fall through to picker only if auto-connect failed
            setConnectMode('picker');
            setConnectOpen(true);
            return;
          }
        }

        setConnectMode('picker');
        setConnectOpen(true);
      } finally {
        inAppBusyRef.current = false;
        setConnectOpening(false);
      }
    },
    [connectAndAuth]
  );

  const requireAuth = useCallback(
    (action, reason) => {
      if (user) {
        action();
        return;
      }
      openConnect(reason || 'Connect your wallet to continue');
    },
    [user, openConnect]
  );

  /**
   * Step 2 (new wallet): reconnect + register with required refer/upline wallet.
   * When a registration fee is set, pass the BNB payment tx hash.
   */
  const register = useCallback(
    async (uplineWallet, walletId = 'injected', txHash = '') => {
      setError('');
      const refer = writeStoredUpline(uplineWallet);
      if (!refer) {
        const err = new Error('A refer wallet address is required');
        err.code = 'INVALID_UPLINE';
        throw err;
      }
      setReferralUplineState(refer);

      const { address: wallet } = await connectWallet(walletId, { forcePermission: true });
      setAddress(wallet);
      await api.checkRegister(wallet, refer);

      const data = await api.register(wallet, refer, txHash);
      setToken(data.token);
      setUser(data.user);
      closeConnect();
      return data.user;
    },
    [closeConnect]
  );

  const disconnect = useCallback(() => {
    setToken('');
    setUser(null);
    setAddress('');
    disconnectWallet();
  }, []);

  const value = useMemo(
    () => ({
      address: user?.wallet_address || address,
      user,
      config,
      loading,
      error,
      setError,
      hasProvider: hasInjectedProvider(),
      connectOpen,
      connectOpening,
      connectReason,
      connectMode,
      setConnectMode,
      pendingWalletId,
      setPendingWalletId,
      openConnect,
      closeConnect,
      requireAuth,
      connectAndAuth,
      register,
      disconnect,
      refreshUser,
      refreshConfig,
      referralUpline,
      setReferralUpline,
    }),
    [
      address,
      user,
      config,
      loading,
      error,
      connectOpen,
      connectOpening,
      connectReason,
      connectMode,
      pendingWalletId,
      openConnect,
      closeConnect,
      requireAuth,
      connectAndAuth,
      register,
      disconnect,
      refreshUser,
      refreshConfig,
      referralUpline,
      setReferralUpline,
    ]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within WalletProvider');
  return ctx;
}
